import { type ReactNode } from 'react'
import {
  Hexagon, Timer, ScanLine, SquareCheckBig, MessageSquare,
  Theater, Package, Star, Globe, BookOpen, DollarSign, Users, Settings,
  Upload, Clock, TrendingDown, AlertTriangle, ScrollText, CreditCard,
  Monitor, Sparkles, ShoppingBag, CalendarRange, CalendarCheck, Bug,
  Building2, Key, Headphones, Ticket, BarChart3, MapPin, Hammer,
  Activity, ToggleLeft, Cable, Network,
  Drama, ClipboardList, Music, ShoppingCart, ArrowDownToLine, ExternalLink,
  Inbox, Route, Boxes, LayoutTemplate, ArrowLeftRight, Printer, Nfc,
  FileText, BookMarked, Terminal,
} from 'lucide-react'

// ── Nav item types ──────────────────────────────────────────────────────────────

/** A leaf navigation entry (rendered as NavLink or button). */
export interface NavItem {
  to: string
  key: string
  icon?: ReactNode
  role?: string
  tooltip?: string
  /** True when the item is gated by a module/plan and should render locked. */
  locked?: boolean
  /** Which module key gates this item. */
  moduleKey?: string
  /** 'plan' | 'module' — which gate type is active. */
  lockReason?: string
  /** Whether this top-level item respects exact-match routing (used in PLATFORM_NAV). */
  end?: boolean
}

/** A non-clickable section header rendered between group children (no nav key — keyed by index in render). */
export interface NavSubHeader {
  subHeader: string
}

/** Union of all child node types in a nav group. */
export type NavChild = NavItem | NavSubHeader

/** A group entry with collapsible children (rendered as flyout in the sidebar). */
export interface NavGroup {
  key: string
  icon: ReactNode
  children: NavChild[]
}

// ── Main section (crew-first, top band) ────────────────────────────────────────
export const MAIN_ITEMS: NavItem[] = [
  { to: '/',         icon: <Hexagon size={16} />,        key: 'dashboard' },
  { to: '/my-hours', icon: <Timer size={16} />,          key: 'navMyHours' },
  { to: '/scan',     icon: <ScanLine size={16} />,       key: 'scan' },
  { to: '/todos',    icon: <SquareCheckBig size={16} />, key: 'tasks' },
  { to: '/comms',    icon: <MessageSquare size={16} />,  key: 'navComms' },
  // Brighella v2 (ADR-013 Merge A) — the Calendar Hub absorbs Daily / Schedule
  // / Season / Global / Crew schedule. Single door for time.
  { to: '/calendar', icon: <CalendarCheck size={16} />,  key: 'navCalendarSchedule' },
]

// ── Productions group ──────────────────────────────────────────────────────────
// Brighella v2 (Wave 3b cleanup, Phase 19 audit follow-up) — 13 flat children
// were too many to scan. Children now grouped via `subHeader:` markers that
// SidebarNavGroup renders as section labels between the items.
export const PRODUCTIONS_GROUP: NavGroup = {
  key: 'navProductions',
  icon: <Theater size={16} />,
  children: [
    // No "Scheduling" subheader on the first group — it sits directly
    // under the flyout's "Productions" title and reads as a redundant
    // second line. Shows/Events/Spaces sit straight under the title.
    { to: '/productions',           icon: <Drama size={16} />,           key: 'navShows' },
    { to: '/events',                icon: <CalendarRange size={16} />,   key: 'navEvents' },
    { to: '/spaces',                icon: <Building2 size={16} />,       key: 'navSpaces' },
    { subHeader: 'navSubgroupCasting' },
    { to: '/riders',                icon: <ClipboardList size={16} />,   key: 'ridersNav',                tooltip: 'navTooltipRiders' },
    { to: '/artists',               icon: <Music size={16} />,           key: 'artistsNav' },
    { subHeader: 'navSubgroupFinance' },
    { to: '/budgets',               icon: <DollarSign size={16} />,      key: 'navBudgets' },
    { to: '/department-orders',     icon: <ShoppingCart size={16} />,    key: 'navDepartmentOrders',      tooltip: 'navTooltipDepartmentOrders' },
    { subHeader: 'navSubgroupPartner' },
    { to: '/incoming-productions',  icon: <ArrowDownToLine size={16} />, key: 'incomingTitle',            tooltip: 'navTooltipIncomingProductions' },
    { to: '/external-productions',  icon: <ExternalLink size={16} />,    key: 'externalProductions',      tooltip: 'navTooltipExternalProductions' },
    { to: '/admin/venue-inquiries', icon: <Inbox size={16} />,           key: 'navVenueInquiries',        role: 'admin' },
  ],
}

// ── Touring group (Pro+ module — own group separate from Productions) ──────────
export const TOURING_GROUP: NavGroup = {
  key: 'toursTitle',
  icon: <MapPin size={16} />,
  children: [
    { to: '/tours', icon: <Route size={16} />, key: 'toursNav' },
  ],
}

// ── Crew group (Brighella v2 Merge C absorbed into People Hub) ───────────────
// Crew/Schedule absorbed into the Calendar Hub (Merge A); Crew/Workload now
// lives as a tab on /people (Merge C). The CREW_GROUP export stays empty so
// AppSidebar can keep dispatching by group key without crashing — sidebar
// renders the group only when it has visible children.
export const CREW_GROUP: NavGroup = {
  key: 'navCrew',
  icon: <Users size={16} />,
  children: [],
}

// ── Inventory group (admin-only items tagged with role:'admin') ─────────────────
export const INVENTORY_GROUP: NavGroup = {
  key: 'navInventory',
  icon: <Package size={16} />,
  children: [
    { to: '/items',              icon: <Package size={16} />,        key: 'navItems' },
    { to: '/wishlist',           icon: <Star size={16} />,           key: 'navWishlist' },
    { to: '/bundles',            icon: <Boxes size={16} />,          key: 'navBundles' },
    { to: '/locations',          icon: <MapPin size={16} />,         key: 'navLocations' },
    { to: '/templates',          icon: <LayoutTemplate size={16} />, key: 'navTemplates' },
    // Brighella v2 (ADR-013 Merge E) — Movements Hub absorbs all loan
    // directions. /loans, /borrowed, /network/loans redirect to /movements.
    { to: '/movements',          icon: <ArrowLeftRight size={16} />, key: 'navMovements' },
    { to: '/maintenance',        icon: <Hammer size={16} />,         key: 'navMaintenance' },
    { to: '/items/print-center', icon: <Printer size={16} />,        key: 'printCenter' },
    { to: '/nfc-tags',           icon: <Nfc size={16} />,            key: 'navNfcTags' },
    { to: '/cables',             icon: <Cable size={16} />,          key: 'navCables' },
    { to: '/tenant/site-templates',     icon: <LayoutTemplate size={16} />, key: 'navSiteTemplates',     role: 'admin', tooltip: 'navTooltipSiteTemplates' },
    { to: '/tenant/vendor-credentials', icon: <Key size={16} />,            key: 'navVendorCredentials', role: 'admin', tooltip: 'navTooltipVendorCredentials' },
    // admin-only
    { to: '/items/import',       icon: <Upload size={16} />,         key: 'navImport',      role: 'admin' },
    { to: '/admin/stale',        icon: <Clock size={16} />,          key: 'navStaleItems',  role: 'admin', tooltip: 'navTooltipStaleItems' },
    { to: '/admin/low-stock',    icon: <TrendingDown size={16} />,   key: 'navLowStock',    role: 'admin' },
    { to: '/admin/lost-damaged', icon: <AlertTriangle size={16} />,  key: 'navLostDamaged', role: 'admin' },
  ],
}

// ── Network group ──────────────────────────────────────────────────────────────
// Wave 3b cleanup — Borrowed/Outgoing direction filters live as quick-jumps
// inside the Movements hub itself; sidebar no longer duplicates them.
export const NETWORK_GROUP: NavGroup = {
  key: 'navNetwork',
  icon: <Globe size={16} />,
  children: [
    { to: '/network',           icon: <Globe size={16} />,         key: 'navNetworkDashboard',   tooltip: 'navTooltipNetwork' },
    { to: '/sites',             icon: <Network size={16} />,       key: 'navNetworkInventory',   tooltip: 'navTooltipNetworkInventory' },
    { to: '/marketplace',       icon: <ShoppingBag size={16} />,   key: 'navMarketplace' },
    { to: '/network/requests',  icon: <ClipboardList size={16} />, key: 'navNeedsBoard' },
  ],
}

// ── Library group ──────────────────────────────────────────────────────────────
export const LIBRARY_GROUP: NavGroup = {
  key: 'navLibrary',
  icon: <BookOpen size={16} />,
  children: [
    { to: '/documents',  icon: <FileText size={16} />,   key: 'documents' },
    { to: '/handbook',   icon: <BookOpen size={16} />,   key: 'handbookTitle' },
    { to: '/green-book', icon: <BookMarked size={16} />, key: 'navGreenBook',  tooltip: 'navTooltipGreenBook' },
  ],
}

// ── Reports hub (Brighella v2 Merge D — single /reports door) ──────────────
// Wave 3b cleanup — sidebar no longer duplicates the 11 view buttons; the
// Reports hub itself surfaces them as a tablist. This is a top-level item,
// not a group. FINANCE_GROUP retained as alias for tests/MobileMenu that
// still iterate the children.
export const REPORTS_ITEM: NavItem = {
  to: '/reports',
  icon: <DollarSign size={16} />,
  key: 'navReports',
}

export const FINANCE_GROUP: NavGroup = {
  key: 'navReports',
  icon: <DollarSign size={16} />,
  children: [
    { to: '/reports?type=financial&view=overview',          key: 'navOverview' },
    { to: '/reports?type=financial&view=explorer',          key: 'navExplorer' },
    { to: '/reports?type=financial&view=insurance',         key: 'navInsurance' },
    { to: '/reports?type=financial&view=forecast',          key: 'navForecast' },
    { to: '/reports?type=financial&view=utilization',       key: 'navUtilization' },
    { to: '/reports?type=financial&view=cash-flow',         key: 'navCashFlow' },
    { to: '/reports?type=financial&view=season-comparison', key: 'navSeasonComparison' },
    { to: '/reports?type=financial&view=petty-cash',        key: 'navPettyCash' },
    { to: '/reports?type=financial&view=budget-revisions',  key: 'navBudgetRevisions' },
    { to: '/reports?type=financial&view=cost-comparison',   key: 'navCostComparison' },
    { to: '/reports?type=financial&view=depreciation',      key: 'navDepreciation' },
  ],
}

// ── People hub (Brighella v2 Merge C — single /people door) ────────────────
// Wave 3b cleanup — directory/availability/workload/absences/timesheets all
// live as tabs on /people; sidebar no longer duplicates them. Legacy entries
// (/users, /admin/pto, /admin/freelancer-timesheets, /crew/workload) still
// redirect to the matching tab. PEOPLE_GROUP retained as alias for tests.
export const PEOPLE_ITEM: NavItem = {
  to: '/people',
  icon: <Users size={16} />,
  key: 'navPeople',
}

// Season Canvas (revived AKOS) is no longer a standalone door — it is the
// default 'season' tab of the People Hub (Merge C+). The deep link lives in
// PEOPLE_GROUP.children below; /season-overview redirects to /people.

export const PEOPLE_GROUP: NavGroup = {
  key: 'navPeople',
  icon: <Users size={16} />,
  children: [
    { to: '/people',                      key: 'navSeasonOverview' },
    { to: '/people?tab=directory',        key: 'navPeopleDirectory' },
    { to: '/people?horizon=availability', key: 'navAvailability' },
    { to: '/people?horizon=workload',     key: 'navCrewWorkload' },
    { to: '/people?tab=absences',         key: 'navPtoInbox',              role: 'admin' },
    { to: '/people?tab=timesheets',       key: 'navFreelancerTimesheets',  role: 'admin' },
  ],
}

// ── Admin group ────────────────────────────────────────────────────────────────
export const ADMIN_GROUP: NavGroup = {
  key: 'navAdmin',
  icon: <Settings size={16} />,
  children: [
    { to: '/admin',            icon: <Settings size={16} />,   key: 'navSettings' },
    { to: '/admin/ai',         icon: <Sparkles size={16} />,   key: 'navAiCapabilities' },
    { to: '/admin/audit-log',  icon: <ScrollText size={16} />, key: 'navAuditLog' },
    { to: '/admin/api-keys',   icon: <Key size={16} />,        key: 'navApiKeys' },
    { to: '/kiosk',            icon: <Monitor size={16} />,    key: 'kioskMode' },
    { to: '/settings/billing', icon: <CreditCard size={16} />, key: 'navBilling' },
  ],
}

// ── Platform nav (global admin mode) ──────────────────────────────────────────
export const PLATFORM_NAV: NavItem[] = [
  { to: '/super-admin',                icon: <Building2 size={16} />,     key: 'navPlatformCompanies',    end: true },
  { to: '/super-admin/admin-accounts', icon: <Key size={16} />,           key: 'navPlatformAdminAccounts' },
  { to: '/super-admin/feedback',       icon: <MessageSquare size={16} />, key: 'navPlatformFeedback' },
  { to: '/super-admin/qa-reports',     icon: <Bug size={16} />,           key: 'navPlatformQaReports' },
  { to: '/super-admin/support',        icon: <Headphones size={16} />,    key: 'navPlatformSupport' },
  { to: '/super-admin/invite-codes',   icon: <Ticket size={16} />,        key: 'navPlatformInviteCodes' },
  { to: '/super-admin/billing',        icon: <CreditCard size={16} />,    key: 'navPlatformBilling' },
  { to: '/super-admin/metrics',        icon: <BarChart3 size={16} />,     key: 'navPlatformMetrics' },
  { to: '/super-admin/onboarding',     icon: <Sparkles size={16} />,      key: 'navPlatformOnboarding' },
  { to: '/super-admin/progress',       icon: <Activity size={16} />,      key: 'navPlatformProgress' },
  { to: '/super-admin/modules',        icon: <ToggleLeft size={16} />,    key: 'navPlatformModules' },
  { to: '/super-admin/demo-modules',   icon: <Monitor size={16} />,       key: 'navPlatformDemoModules' },
  { to: '/super-admin/system-health',  icon: <Activity size={16} />,      key: 'navPlatformHealth' },
  { to: '/super-admin/usage',          icon: <BarChart3 size={16} />,     key: 'navPlatformUsage' },
  { to: '/super-admin/logs',           icon: <Terminal size={16} />,      key: 'navPlatformLogs' },
]

// ── Mobile bottom nav (5 tabs — mirrors Main) ──────────────────────────────────
export const MOBILE_NAV: NavItem[] = [
  { to: '/',         icon: <Hexagon size={18} />,        key: 'dashboard' },
  { to: '/my-hours', icon: <Timer size={18} />,          key: 'navMyHours' },
  { to: '/scan',     icon: <ScanLine size={18} />,       key: 'scan' },
  { to: '/todos',    icon: <SquareCheckBig size={18} />, key: 'tasks' },
  { to: '/comms',    icon: <MessageSquare size={18} />,  key: 'navComms' },
]

// ── Shared type icons (used by search results) ─────────────────────────────────
export const TYPE_ICONS: Record<string, ReactNode> = {
  item:       <Package size={16} />,
  production: <Theater size={16} />,
  bundle:     <Package size={16} />,
  location:   <Globe size={16} />,
  user:       <Users size={16} />,
  document:   <BookOpen size={16} />,
}

// ── Backward-compat aliases (consumed by AppSidebar, AppLayout, MobileMenu, qaTreeBuilder)
// These will be removed once those files are updated in later tasks.
export const FINANCIAL_GROUP = FINANCE_GROUP

export const NAV_ITEMS_KEYS = MAIN_ITEMS

export const ADMIN_ITEMS_KEYS: NavItem[] = [
  { to: '/users',            icon: <Users size={16} />,         key: 'navUsers' },
  { to: '/admin',            icon: <Settings size={16} />,      key: 'navSettings' },
  { to: '/admin/pto',        icon: <CalendarCheck size={16} />, key: 'navPtoInbox' },
  { to: '/items/import',     icon: <Upload size={16} />,        key: 'navImport' },
  { to: '/admin/stale',      icon: <Clock size={16} />,         key: 'navStaleItems' },
  { to: '/admin/low-stock',  icon: <TrendingDown size={16} />,  key: 'navLowStock' },
  { to: '/admin/lost-damaged', icon: <AlertTriangle size={16} />, key: 'navLostDamaged' },
  { to: '/admin/audit-log',  icon: <ScrollText size={16} />,    key: 'navAuditLog' },
  { to: '/admin/ai',         icon: <Sparkles size={16} />,      key: 'navAiCapabilities' },
  { to: '/settings/billing', icon: <CreditCard size={16} />,    key: 'navBilling' },
]

export const GLOBAL_ADMIN_ITEMS: NavItem[] = [
  { to: '/super-admin',            icon: <Globe size={16} />, key: 'navSuperAdmin' },
  { to: '/super-admin/qa-reports', icon: <Bug size={16} />,   key: 'navQaReports' },
]
